// Card Personalisation Handler
// Intercepts form submissions for personalised cards and ecards to redirect to personalisation page

class CardPersonalisationHandler {
  constructor() {
    this.init();
  }

  init() {
    document.addEventListener('DOMContentLoaded', () => {
      this.attachEventListeners();
    });
  }

  attachEventListeners() {
    console.log('Card Personalisation: Attaching event listeners');
    
    // Listen for product form submissions
    document.addEventListener('submit', (event) => {
      const form = event.target;
      console.log('Card Personalisation: Form submit detected:', form.id, form.className);
      
      // Check if this is a product form (try multiple patterns)
      if (form.matches('[data-type="add-to-cart-form"]') || 
          form.matches('[is="product-form"]') ||
          form.id.includes('product-form') ||
          form.querySelector('[name="add"]')) {
        console.log('Card Personalisation: Product form identified, checking for personalisation');
        this.handleProductFormSubmit(event, form);
      }
    });

    // Also listen for AJAX add-to-cart attempts (for card components)
    document.addEventListener('click', (event) => {
      const button = event.target.closest('[type="submit"][name="add"]');
      if (button) {
        console.log('Card Personalisation: Add to cart button clicked:', button);
        
        const form = button.closest('form') || button.form;
        if (form) {
          console.log('Card Personalisation: Found form for button:', form.id);
          // Small delay to let variant selection update
          setTimeout(() => {
            this.checkForCardPersonalisation(event, form, button);
          }, 50);
        } else {
          console.log('Card Personalisation: No form found for button');
        }
      }
    });
    
    // Backup: Listen for any button clicks with add-to-cart behavior
    document.addEventListener('click', (event) => {
      const button = event.target.closest('button');
      if (button && (button.textContent.includes('Add to cart') || button.textContent.includes('Personalise'))) {
        console.log('Card Personalisation: Potential add-to-cart button clicked:', button.textContent);
        
        const form = button.closest('form') || button.form;
        if (form) {
          console.log('Card Personalisation: Checking form for personalisation requirement');
          setTimeout(() => {
            if (this.requiresPersonalisation(form)) {
              console.log('Card Personalisation: Preventing default and redirecting');
              event.preventDefault();
              event.stopImmediatePropagation();
              this.redirectToPersonalisation(form);
            }
          }, 10);
        }
      }
    });
  }

  handleProductFormSubmit(event, form) {
    // Check if this requires personalisation
    if (this.requiresPersonalisation(form)) {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.redirectToPersonalisation(form);
    }
  }

  checkForCardPersonalisation(event, form, button) {
    if (this.requiresPersonalisation(form)) {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.redirectToPersonalisation(form);
    }
  }

  requiresPersonalisation(form) {
    try {
      console.log('Card Personalisation: Checking if personalisation is required for form:', form.id);
      console.log('Card Personalisation: Form dataset requiresPersonalisation:', form.dataset.requiresPersonalisation);
      
      // Priority Method: Check if the form has been explicitly marked as requiring personalisation
      if (form.dataset.requiresPersonalisation === 'true') {
        console.log('Card Personalisation: Form explicitly marked as requiring personalisation');
        return true;
      }
      
      // Get the selected variant from the form
      const variantIdInput = form.querySelector('input[name="id"]');
      if (!variantIdInput || !variantIdInput.value) {
        console.log('Card Personalisation: No variant ID found');
        return false;
      }
      
      console.log('Card Personalisation: Current variant ID:', variantIdInput.value);

      // Method 1: Check card variant picker selections
      const cardVariantPicker = document.querySelector('.card-variant-picker');
      if (cardVariantPicker) {
        const selectedInputs = cardVariantPicker.querySelectorAll('input[type="radio"]:checked');
        console.log('Card Personalisation: Found', selectedInputs.length, 'selected card options');
        
        for (const input of selectedInputs) {
          const value = input.value.toLowerCase();
          console.log('Card Personalisation: Checking card option:', value);
          
          if (value.includes('personalised') || 
              value.includes('e-card') || 
              value.includes('ecard')) {
            console.log('Card Personalisation: Found personalisation requirement for:', value);
            return true;
          }
        }
      }

      // Method 2: Check any form inputs with card-related names
      const cardTypeInputs = form.querySelectorAll('input[name*="options"], select[name*="options"]');
      console.log('Card Personalisation: Found', cardTypeInputs.length, 'option inputs');
      
      // Check if this is a BUNDLE product - bundles with print-at-home should NOT require personalisation
      const bundleCheckTitle = this.getProductTitle(form);
      const isBundleProduct = bundleCheckTitle && bundleCheckTitle.toLowerCase().includes('bundle');
      
      for (const input of cardTypeInputs) {
        if (input.checked || input.selected || (input.type !== 'radio' && input.type !== 'checkbox' && input.value)) {
          const value = input.value.toLowerCase();
          console.log('Card Personalisation: Checking option input:', value);
          
          // Check for print-at-home variants
          const isPrintAtHome = value.includes('print-at-home') || value.includes('print at home');
          
          // Skip personalisation for BUNDLE products with print-at-home variants
          if (isBundleProduct && isPrintAtHome) {
            console.log('Card Personalisation: BUNDLE product with Print at Home - skipping personalisation');
            continue; // Skip this option, check others
          }
          
          if (value.includes('personalised') || 
              value.includes('e-card') || 
              value.includes('ecard') ||
              isPrintAtHome) {
            console.log('Card Personalisation: Found personalisation requirement for:', value);
            return true;
          }
        }
      }

      // Method 3: Check if the form has been marked as requiring personalisation
      if (form.dataset.requiresPersonalisation === 'true') {
        console.log('Card Personalisation: Form marked as requiring personalisation');
        return true;
      }

      // Method 4: Check product title or type for card-related products
      const productTitle = this.getProductTitle(form);
      if (productTitle) {
        const titleLower = productTitle.toLowerCase();
        console.log('Card Personalisation: Checking product title:', titleLower);
        
        if ((titleLower.includes('card') || titleLower.includes('ecard')) && 
            titleLower.includes('personalised')) {
          console.log('Card Personalisation: Found personalisation requirement in product title:', productTitle);
          return true;
        }
      }

      console.log('Card Personalisation: No personalisation requirement found');
      return false;
    } catch (error) {
      console.error('Card Personalisation: Error checking personalisation requirement:', error);
      return false;
    }
  }

  getProductTitle(form) {
    try {
      // Try to find product title in various locations
      const productContainer = form.closest('.product') || form.closest('[data-section]');
      
      if (productContainer) {
        const titleElement = productContainer.querySelector('.product__title, .card-personalisation-product__info h3, h1, h2');
        if (titleElement) {
          return titleElement.textContent.trim();
        }
      }

      // Check if there's a product info section
      const sectionId = form.id.replace('product-form-', '');
      if (sectionId) {
        const productInfo = document.querySelector(`[data-section="${sectionId}"]`);
        if (productInfo) {
          const titleElement = productInfo.querySelector('.product__title, h1, h2');
          if (titleElement) {
            return titleElement.textContent.trim();
          }
        }
      }

      return null;
    } catch (error) {
      console.error('Card Personalisation: Error getting product title:', error);
      return null;
    }
  }

  redirectToPersonalisation(form) {
    try {
      console.log('Card Personalisation: Redirecting to personalisation page');
      console.log('Card Personalisation: Form:', form);
      
      // Debug: Check all inputs in the form
      const allInputs = form.querySelectorAll('input, select, textarea');
      console.log('Card Personalisation: All form inputs:', Array.from(allInputs).map(input => ({
        name: input.name,
        value: input.value,
        type: input.type,
        checked: input.checked
      })));
      
      // Get form data
      const formData = new FormData(form);
      let variantId = formData.get('id');
      const quantity = formData.get('quantity') || '1';
      
      // Debug: Check specific variant ID input
      const variantIdInput = form.querySelector('input[name="id"]');
      console.log('Card Personalisation: Variant ID input element:', variantIdInput);
      console.log('Card Personalisation: Variant ID input value:', variantIdInput ? variantIdInput.value : 'NOT FOUND');
      
      // Get product ID using multiple methods
      let productId = this.getProductId(form);
      
      console.log('Card Personalisation: Found product ID:', productId, 'variant ID:', variantId);
      
      // Additional check: if variant ID is empty, try to get it from the card variant picker
      if (!variantId || variantId.trim() === '') {
        console.log('Card Personalisation: Variant ID is empty, checking card variant picker...');
        const cardPicker = document.querySelector('.card-variant-picker');
        if (cardPicker) {
          const selectedInputs = cardPicker.querySelectorAll('input[type="radio"]:checked');
          console.log('Card Personalisation: Selected card options:', Array.from(selectedInputs).map(i => i.value));
          
                     // Try to find the matching variant
           const selectedValues = Array.from(selectedInputs).map(input => input.value);
           const matchingVariant = findMatchingVariant(selectedValues);
           if (matchingVariant) {
             console.log('Card Personalisation: Using variant from card picker:', matchingVariant.id);
             variantId = matchingVariant.id;
           }
        }
      }

      // Final debug output
      console.log('Card Personalisation: Final values - product ID:', productId, 'variant ID:', variantId);

      // Validate we have the required data
      if (!variantId || variantId.trim() === '') {
        console.error('Card Personalisation: No variant ID found - cannot redirect to personalisation page');
        console.error('❌ No variant selected. Please select a card variant first.');
        return;
      }
      
      if (!productId) {
        console.warn('Card Personalisation: No product ID found - proceeding with variant ID only');
      }

      // Create the personalisation URL
      let personalisationUrl = '/pages/card-personalisation';
      
      // Add parameters
      const params = new URLSearchParams();
      if (variantId) params.append('variant_id', variantId);
      if (quantity) params.append('quantity', quantity);
      if (productId) params.append('product_id', productId);
      
      if (params.toString()) {
        personalisationUrl += '?' + params.toString();
      }

      // For POST data, we'll use a form submission since URL params might be too long
      this.submitPersonalisationForm(productId, variantId, quantity);
      
    } catch (error) {
      console.error('Card Personalisation: Error redirecting to personalisation:', error);
      // Fall back to normal form submission
      this.fallbackSubmit(form);
    }
  }

  submitPersonalisationForm(productId, variantId, quantity) {
    // Build URL with parameters
    const params = new URLSearchParams();
    
    if (productId) {
      params.append('product_id', productId);
    }
    if (variantId) {
      params.append('variant_id', variantId);
    }
    if (quantity) {
      params.append('quantity', quantity);
    }
    
    // Build the final URL
    const personalisationUrl = `/pages/card-personalisation?${params.toString()}`;
    
    console.log('Card Personalisation: Redirecting to:', personalisationUrl);
    
    // Redirect to the personalisation page
    window.location.href = personalisationUrl;
  }

  fallbackSubmit(form) {
    console.log('Card Personalisation: Falling back to normal form submission');
    
    // Remove our event listeners temporarily
    const newForm = form.cloneNode(true);
    form.parentNode.replaceChild(newForm, form);
    
    // Submit the new form
    newForm.submit();
  }

  getProductId(form) {
    console.log('Card Personalisation: Getting product ID for form:', form.id);
    
    // Method 1: From form data attributes  
    if (form.dataset.productId) {
      console.log('Card Personalisation: Found product ID from form data-product-id:', form.dataset.productId);
      return form.dataset.productId;
    }
    
    // Method 2: From main product info container
    const mainProduct = document.querySelector('product-info[data-product-id]');
    if (mainProduct && mainProduct.dataset.productId) {
      console.log('Card Personalisation: Found product ID from main product info:', mainProduct.dataset.productId);
      return mainProduct.dataset.productId;
    }
    
    // Method 3: From parent container data attributes
    const productContainer = form.closest('[data-product-id]');
    if (productContainer && productContainer.dataset.productId) {
      console.log('Card Personalisation: Found product ID from parent container:', productContainer.dataset.productId);
      return productContainer.dataset.productId;
    }
    
    // Method 4: From current page URL (if on product page)
    const currentPath = window.location.pathname;
    if (currentPath.includes('/products/')) {
      // Try to get from window.product if available (common in Shopify themes)
      if (window.product && window.product.id) {
        console.log('Card Personalisation: Found product ID from window.product:', window.product.id);
        return window.product.id.toString();
      }
      
      // Try to get from shopify object
      if (window.ShopifyAnalytics && window.ShopifyAnalytics.meta && window.ShopifyAnalytics.meta.product) {
        const productId = window.ShopifyAnalytics.meta.product.id;
        console.log('Card Personalisation: Found product ID from Shopify analytics:', productId);
        return productId.toString();
      }
    }
    
    console.warn('Card Personalisation: Could not determine product ID');
    return null;
  }
}

  // Enhanced card variant picker integration
document.addEventListener('DOMContentLoaded', function() {
  console.log('🚀 Card Personalisation: DOM loaded, initializing...');
  
  // Initialize the card personalisation handler
  window.cardPersonalisationHandler = new CardPersonalisationHandler();
  
  // Wait a bit for elements to be ready, then set up card variant picker integration
  setTimeout(() => {
    console.log('🔧 Card Personalisation: Setting up integration');
    setupCardVariantPickerIntegration();
    
    // Also set up direct variant detection immediately as backup
    console.log('🔧 Card Personalisation: Setting up direct variant detection backup');
    setTimeout(() => {
      setupDirectVariantDetection();
    }, 200);
  }, 100);
  
  // Add immediate debugging
  setTimeout(() => {
    console.log('🔍 Card Personalisation: Running initial debug check...');
    debugCardPersonalisation();
  }, 2000);
  
  // Form monitoring is handled by direct variant detection when needed
});

// Removed setupAggressiveFormMonitoring - functionality moved to setupDirectVariantDetection

function setupCardVariantPickerIntegration() {
  console.log('Card Personalisation: Setting up card variant picker integration');
  
  let setupComplete = false;
  
  // Also watch for dynamically added elements
  const observer = new MutationObserver((mutations) => {
    if (setupComplete) return;
    
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === 1) { // Element node
          const isVariantSelects = node.tagName === 'VARIANT-SELECTS' || 
                                   node.querySelector && node.querySelector('variant-selects');
          if (isVariantSelects) {
            console.log('Card Personalisation: 🚀 Detected variant-selects element added to DOM!');
            setTimeout(() => {
              if (!setupComplete) {
                waitForCardPicker();
              }
            }, 100);
          }
        }
      });
    });
  });
  
  observer.observe(document.body, {
    childList: true,
    subtree: true
  });
  
  // Wait for card variant picker to be fully loaded
  const waitForCardPicker = () => {
    // Try multiple selectors to find card variant pickers
    const selectors = [
      '.card-variant-picker',
      'variant-selects.card-variant-picker',
      '[class*="card-variant-picker"]'
    ];
    
    let cardVariantPickers = [];
    for (const selector of selectors) {
      const found = document.querySelectorAll(selector);
      if (found.length > 0) {
        cardVariantPickers = found;
        console.log(`Card Personalisation: Found ${found.length} card variant pickers using selector: ${selector}`);
        break;
      }
    }
    
    // Only log detailed debug on first few attempts
    if (!waitForCardPicker.retryCount || waitForCardPicker.retryCount <= 2) {
      console.log('Card Personalisation: Found', cardVariantPickers.length, 'card variant pickers total');
      
      // Debug what's actually on the page
      const allVariantSelects = document.querySelectorAll('variant-selects');
      console.log('Card Personalisation: DEBUG - All variant-selects elements:', allVariantSelects.length);
      if (allVariantSelects.length > 0) {
        allVariantSelects.forEach((el, i) => {
          console.log(`Card Personalisation: DEBUG - variant-selects[${i}]:`, el.className, el.id);
        });
      }
    }
    
    if (cardVariantPickers.length === 0) {
      // Increase retry frequency for dynamic content
      const retryCount = waitForCardPicker.retryCount || 0;
      waitForCardPicker.retryCount = retryCount + 1;
      
            if (retryCount < 5) { // Reduced to 1 second max
        if (retryCount === 1) { // Only log once
          console.log(`Card Personalisation: No card variant pickers found, retrying (${retryCount + 1}/5)...`);
        }
        setTimeout(waitForCardPicker, 200);
      } else {
        console.log('Card Personalisation: ⚠️ Card variant picker not found after 1 second.');
        console.log('Card Personalisation: 🎯 Switching to direct variant detection...');
        setupDirectVariantDetection();
      }
      return;
    }
    
    console.log('Card Personalisation: ✅ Card variant pickers loaded, setting up integration...');
    
    // Mark setup as complete and disconnect observer
    setupComplete = true;
    observer.disconnect();
    
    // ALWAYS set up direct variant detection as backup for form interception
    console.log('Card Personalisation: 🔄 Also setting up direct variant detection as backup...');
    setTimeout(() => {
      setupDirectVariantDetection();
    }, 100);
    
    // Listen for card variant picker changes to update personalisation requirements
    Array.from(cardVariantPickers).forEach(picker => {
    picker.addEventListener('change', function(event) {
      const input = event.target;
      console.log('Card Personalisation: Card variant picker changed:', input.value);
      
      if (input.type === 'radio') {
        const value = input.value.toLowerCase();
        const productForm = input.form;
        
        console.log('Card Personalisation: Selected value:', value, 'Form:', productForm?.id);
        
        // CRITICAL: Update the variant ID in the main product form
        updateMainFormVariantId(picker, productForm);
        
        if (productForm) {
          // Check if this is a card type change (personalised/e-card) or impact amount change
          const isCardTypeOption = value.includes('personalised') || value.includes('e-card') || value.includes('ecard') || value.includes('blank') || value.includes('print at home') || value.includes('donation');
          const isImpactAmountOption = /^\$?\d+/.test(value); // Check if value starts with $ and/or digits
          
          if (isCardTypeOption) {
            // Check if this is a BUNDLE product - bundles with print-at-home should NOT require personalisation
            const isBundleProduct = checkIfBundleProduct();
            const isPrintAtHome = value.includes('print-at-home') || value.includes('print at home');
            
            // This is a card type selection - update personalisation requirement
            if (value.includes('personalised') || value.includes('e-card') || value.includes('ecard') || (isPrintAtHome && !isBundleProduct)) {
              productForm.dataset.requiresPersonalisation = 'true';
              console.log('Card Personalisation: ✅ Marked form as requiring personalisation for:', value);
              
              // Use the proper button update function
              updateSubmitButtonText(productForm, 'Personalise Card');
              
              // Hide quantity selector for ecard/personalised variants
              hideQuantitySelector(productForm);
              
            } else {
              // For blank card, bundle print at home, donation, etc. - no personalisation required
              productForm.dataset.requiresPersonalisation = 'false';
              console.log('Card Personalisation: ❌ Marked form as NOT requiring personalisation for:', value, isBundleProduct ? '(BUNDLE product)' : '');
              
              // Use the proper button update function to restore original text
              updateSubmitButtonText(productForm, null);
              
              // Show quantity selector for other variants (like blank card, print at home)
              showQuantitySelector(productForm);
            }
          } else if (isImpactAmountOption) {
            // This is an impact amount change - preserve existing personalisation state
            const currentRequiresPersonalisation = productForm.dataset.requiresPersonalisation === 'true';
            console.log('Card Personalisation: Impact amount changed to:', value, '- preserving personalisation state:', currentRequiresPersonalisation);
            
            if (currentRequiresPersonalisation) {
              // Keep the personalise button and hidden quantity
              updateSubmitButtonText(productForm, 'Personalise Card');
              hideQuantitySelector(productForm);
            } else {
              // Keep the add to cart button and visible quantity
              updateSubmitButtonText(productForm, null);
              showQuantitySelector(productForm);
            }
          }
        }
      }
    });
  });
  
  // Also check initial state
  cardVariantPickers.forEach(picker => {
    console.log('Card Personalisation: Checking initial state for picker');
    const checkedInputs = picker.querySelectorAll('input[type="radio"]:checked');
    console.log('Card Personalisation: Found', checkedInputs.length, 'initially checked inputs');
    
    checkedInputs.forEach(input => {
      console.log('Card Personalisation: Initially checked option:', input.value);
      const event = new Event('change', { bubbles: true });
      Object.defineProperty(event, 'target', { value: input });
      input.dispatchEvent(event);
    });
    
    // Force check the current state immediately
    const allInputs = picker.querySelectorAll('input[type="radio"]:checked');
    const selectedValues = Array.from(allInputs).map(i => i.value.toLowerCase());
    console.log('Card Personalisation: Initial selected values:', selectedValues);
    
    // Check if this is a BUNDLE product - bundles with print-at-home should NOT require personalisation
    const isBundleProduct = checkIfBundleProduct();
    const hasPrintAtHome = selectedValues.some(value => 
      value.includes('print-at-home') || value.includes('print at home')
    );
    
    // Skip personalisation for BUNDLE products with print-at-home variants
    let requiresPersonalisation;
    if (isBundleProduct && hasPrintAtHome) {
      console.log('Card Personalisation: BUNDLE product with Print at Home - skipping personalisation');
      requiresPersonalisation = false;
    } else {
      requiresPersonalisation = selectedValues.some(value => 
        value.includes('personalised') || 
        value.includes('e-card') || 
        value.includes('ecard') ||
        value.includes('e card') ||
        value.includes('print-at-home') ||
        value.includes('print at home')
      );
    }
    
    console.log('Card Personalisation: Initial state requires personalisation:', requiresPersonalisation, isBundleProduct ? '(BUNDLE product detected)' : '');
    
    if (requiresPersonalisation) {
      // Find the product form and mark it
      const productForm = picker.querySelector('input[type="radio"]')?.form;
      if (productForm) {
        productForm.dataset.requiresPersonalisation = 'true';
        console.log('Card Personalisation: ✅ Initially marked form as requiring personalisation');
        
        // Update button text
        console.log('Card Personalisation: Looking for submit button in form:', productForm.id);
        const submitButton = productForm.querySelector('button[type="submit"][name="add"]') || 
                            productForm.querySelector('button[type="submit"]') ||
                            productForm.querySelector('.btn[type="submit"]');
        console.log('Card Personalisation: Submit button found:', submitButton);
        
        // Use the proper button update function
        updateSubmitButtonText(productForm, 'Personalise Card');
        
        // Hide quantity selector for ecard/personalised variants
        hideQuantitySelector(productForm);
      }
    } else {
      // No personalisation required - check if we need to show quantity selector for blank cards etc.
      const productForm = picker.querySelector('input[type="radio"]')?.form;
      if (productForm) {
        productForm.dataset.requiresPersonalisation = 'false';
        updateSubmitButtonText(productForm, null);
        // Show quantity selector for other variants (like blank card)
        showQuantitySelector(productForm);
      }
    }
  });
  
  }; // End waitForCardPicker function
  
  // Simplified: Just listen for Shopify section events
  document.addEventListener('shopify:section:load', () => {
    console.log('Card Personalisation: 🔄 Shopify section loaded, checking for variant pickers...');
    setTimeout(() => {
      if (!setupComplete) {
        const variantSelects = document.querySelectorAll('variant-selects');
        if (variantSelects.length > 0) {
          console.log('Card Personalisation: Found variant-selects after section load');
          waitForCardPicker();
        }
      }
    }, 500);
  });
  
  // Start the waiting process
  waitForCardPicker();
}

function updateMainFormVariantId(cardVariantPicker, productForm) {
  try {
    console.log('Card Personalisation: Updating main form variant ID');
    
    // Get all selected options from the card variant picker
    const selectedInputs = cardVariantPicker.querySelectorAll('input[type="radio"]:checked');
    const selectedValues = Array.from(selectedInputs).map(input => input.value);
    
    console.log('Card Personalisation: Selected card options:', selectedValues);
    
    if (selectedValues.length === 0) {
      console.log('Card Personalisation: No options selected');
      return;
    }
    
    // Find the matching variant for these options
    const matchingVariant = findMatchingVariant(selectedValues);
    
    if (matchingVariant) {
      console.log('Card Personalisation: Found matching variant:', matchingVariant);
      
      // Update the main form's variant ID
      const variantIdInput = productForm.querySelector('input[name="id"]');
      if (variantIdInput) {
        console.log('Card Personalisation: Updating variant ID from', variantIdInput.value, 'to', matchingVariant.id);
        variantIdInput.value = matchingVariant.id;
        
        // Trigger change event to notify other systems
        variantIdInput.dispatchEvent(new Event('change', { bubbles: true }));
        
        // Also trigger variant change event for the theme system
        document.dispatchEvent(new CustomEvent('variant:changed', {
          detail: { variant: matchingVariant }
        }));
        
        // Publish to FoxTheme pubsub if available
        if (window.FoxTheme && window.FoxTheme.pubsub) {
          window.FoxTheme.pubsub.publish(window.FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, {
            data: {
              variant: matchingVariant,
              sectionId: cardVariantPicker.dataset.section
            }
          });
        }
      } else {
        console.warn('Card Personalisation: No variant ID input found in form');
      }
    } else {
      console.warn('Card Personalisation: No matching variant found for options:', selectedValues);
    }
  } catch (error) {
    console.error('Card Personalisation: Error updating main form variant ID:', error);
  }
}

function findMatchingVariant(selectedValues) {
  try {
    // Method 1: Try to get variants from card variant picker data
    const cardVariantPicker = document.querySelector('.card-variant-picker');
    if (cardVariantPicker) {
      const variantsScript = cardVariantPicker.querySelector('script[data-product-variants]');
      if (variantsScript) {
        const variants = JSON.parse(variantsScript.textContent);
        console.log('Card Personalisation: Using card variant picker variants data, found', variants.length, 'variants');
        return findVariantByOptions(variants, selectedValues);
      }
    }
    
    // Method 2: Try to get variants from window.product
    if (window.product && window.product.variants) {
      console.log('Card Personalisation: Using window.product variants');
      return findVariantByOptions(window.product.variants, selectedValues);
    }
    
    // Method 3: Try to get variants from JSON scripts
    const productScripts = document.querySelectorAll('script[type="application/json"]');
    for (const script of productScripts) {
      try {
        const data = JSON.parse(script.textContent);
        if (data && Array.isArray(data) && data[0] && data[0].id && data[0].options) {
          console.log('Card Personalisation: Using JSON script variants');
          return findVariantByOptions(data, selectedValues);
        }
      } catch (e) {
        // Continue searching
      }
    }
    
    console.log('Card Personalisation: No variant data found');
    return null;
  } catch (error) {
    console.error('Card Personalisation: Error finding matching variant:', error);
    return null;
  }
}

function findVariantByOptions(variants, selectedValues) {
  console.log('Card Personalisation: Looking for variant matching options:', selectedValues);
  console.log('Card Personalisation: Available variants:', variants.map(v => ({ id: v.id, title: v.title, options: v.options || [v.option1, v.option2, v.option3].filter(Boolean) })));
  
  return variants.find(variant => {
    // Get variant options (try both formats: .options array or .option1, .option2, etc.)
    const variantOptions = variant.options || [variant.option1, variant.option2, variant.option3].filter(Boolean);
    
    console.log('Card Personalisation: Checking variant', variant.id, 'with options:', variantOptions);
    
    // Check if this variant matches the selected options
    const matches = selectedValues.every((value, index) => {
      const variantOption = variantOptions[index];
      const match = variantOption === value;
      console.log(`Card Personalisation: Option ${index}: "${variantOption}" === "${value}" = ${match}`);
      return match;
    });
    
    if (matches) {
      console.log('Card Personalisation: Found matching variant:', variant.id, variant.title);
    }
    
    return matches;
  });
}

// Alternative approach: Direct variant detection from product data
function setupDirectVariantDetection() {
  console.log('Card Personalisation: 🎯 Setting up direct variant detection (fallback method)');
  
  // Find all product forms - ensure we get form elements, not inputs
  let productForms = Array.from(document.querySelectorAll('form')).filter(form => 
    form.action && form.action.includes('/cart/add')
  );
  console.log('Card Personalisation: Found', productForms.length, 'product forms');
  
  // If no cart/add forms, try broader search including card personalization form
  if (productForms.length === 0) {
    // Try to find the card personalization form specifically
    const cardPersonalizationForm = document.querySelector('form.card-personalization-form');
    if (cardPersonalizationForm) {
      console.log('Card Personalisation: Found card personalization form by class name');
      productForms = [cardPersonalizationForm];
    } else {
      const allForms = document.querySelectorAll('form');
      console.log('Card Personalisation: No cart/add forms found, checking all', allForms.length, 'forms');
      allForms.forEach((form, i) => {
        console.log(`  Form ${i}:`, {
          action: form.action,
          id: form.id,
          method: form.method,
        hasAddButton: !!form.querySelector('button[name="add"]')
        });
      });
      
      // Look for forms with add buttons instead
      const formsWithAddButton = Array.from(allForms).filter(form => 
        form.tagName === 'FORM' && (
          form.querySelector('button[name="add"]') || 
          form.querySelector('button[type="submit"]')
        )
    );
    console.log('Card Personalisation: Found', formsWithAddButton.length, 'forms with add/submit buttons');
    
      if (formsWithAddButton.length > 0) {
        productForms = formsWithAddButton; // Use these forms instead
      }
    }
  }
  
  // Debug: Show all forms on the page
  const allForms = document.querySelectorAll('form');
  console.log('Card Personalisation: DEBUG - All forms on page:', allForms.length);
  allForms.forEach((form, i) => {
    console.log(`  Form ${i}:`, {
      action: form.action,
      id: form.id,
      method: form.method
    });
  });
  
  // Debug: Check current page
  console.log('Card Personalisation: DEBUG - Current page:', window.location.pathname);
  
  productForms.forEach((form, index) => {
    // Double-check we have a proper form element
    if (form && form.tagName === 'FORM') {
      setupFormForDirectDetection(form, index);
    } else {
      console.error('Card Personalisation: Skipping non-form element:', form);
    }
  });
  
  // Add global form submission interception as backup
  console.log('Card Personalisation: Setting up global form submission interception');
  document.addEventListener('submit', function(event) {
    const form = event.target;
    console.log('🚨 GLOBAL: Form submission detected!', form);
    console.log('🚨 GLOBAL: Form details:', {
      id: form.id,
      className: form.className,
      action: form.action,
      method: form.method
    });
    
    // Check for card personalization form
    
    if (form.className && form.className.includes('card-personalization-form')) {
      console.log('🎯 GLOBAL: Card personalization form detected!');
      
      // Handle express shipping for card personalization form
      if (handleExpressShipping(event, form)) {
        console.log('🎯 GLOBAL: Express shipping handler took over');
        return; // Express shipping handler will handle everything
      }
    }
    
    // Check if this is a cart/add form
    if (form.action && form.action.includes('/cart/add')) {
      console.log('Card Personalisation: 🌐 Global form submission intercepted:', form.id);
      
      // Get variants data
      const productVariantsScript = form.querySelector('script[data-product-variants]') || 
                                    document.querySelector('script[data-product-variants]');
      
      if (productVariantsScript) {
        try {
          const variants = JSON.parse(productVariantsScript.textContent);
          const variantId = getSelectedVariantId(form);
          const productTitle = getProductTitleFromPage();
          const requiresPersonalisation = variantId && requiresPersonalisationByVariantId(variantId, variants, productTitle);
          
          console.log('Card Personalisation: Global check - variant:', variantId, 'requires:', requiresPersonalisation);
          
          if (requiresPersonalisation) {
            console.log('Card Personalisation: 🚨 GLOBAL INTERCEPTION - Stopping form submission and redirecting');
            event.preventDefault();
            event.stopImmediatePropagation();
            redirectToPersonalisationPage(form, variantId);
            return false;
          }
        } catch (e) {
          console.error('Card Personalisation: Error in global interception:', e);
        }
      }
    }
  }, true); // Use capture phase
}

// Set up direct detection for a specific form
function setupFormForDirectDetection(form, index) {
  // Ensure we have a form element
  if (!form || form.tagName !== 'FORM') {
    console.error('Card Personalisation: Invalid form element passed to setupFormForDirectDetection:', form);
    return;
  }
  
  console.log(`Card Personalisation: Setting up form ${index}:`, form.id || '[no id]');
  
  // Get product data from nearby scripts
  const productVariantsScript = form.querySelector('script[data-product-variants]') || 
                                document.querySelector('script[data-product-variants]');
  
  if (productVariantsScript) {
    try {
      const variants = JSON.parse(productVariantsScript.textContent);
      console.log('Card Personalisation: Found', variants.length, 'variants in product data');
      
      // Show sample variants for debugging
      if (variants.length > 0) {
        console.log('Card Personalisation: Sample variants:', 
          variants.slice(0, 3).map(v => ({
            id: v.id,
            title: v.title,
            option1: v.option1,
            option2: v.option2,
            option3: v.option3
          }))
        );
      }
      
      // Check if any variants are personalized cards
      const hasPersonalizedVariants = variants.some(variant => {
        const title = variant.title ? variant.title.toLowerCase() : '';
        const option1 = variant.option1 ? variant.option1.toLowerCase() : '';
        const option2 = variant.option2 ? variant.option2.toLowerCase() : '';
        const option3 = variant.option3 ? variant.option3.toLowerCase() : '';
        
        return title.includes('personalised') || title.includes('e-card') || title.includes('ecard') ||
               option1.includes('personalised') || option1.includes('e-card') || option1.includes('ecard') ||
               option2.includes('personalised') || option2.includes('e-card') || option2.includes('ecard') ||
               option3.includes('personalised') || option3.includes('e-card') || option3.includes('ecard');
      });
      
      if (hasPersonalizedVariants) {
        console.log('Card Personalisation: ✅ Product has personalized variants, setting up interception');
        setupFormInterception(form, variants);
      } else {
        console.log('Card Personalisation: No personalized variants found in this product');
      }
      
    } catch (error) {
      console.error('Card Personalisation: Error parsing product variants:', error);
    }
  } else {
    console.log('Card Personalisation: No product variants script found for form', index);
    
    // Try to find variants data elsewhere
    const allScripts = document.querySelectorAll('script[type="application/json"]');
    console.log('Card Personalisation: Looking through', allScripts.length, 'JSON scripts for variant data');
    
    for (const script of allScripts) {
      try {
        const data = JSON.parse(script.textContent);
        if (Array.isArray(data) && data[0] && data[0].id && data[0].title) {
          console.log('Card Personalisation: Found potential variant data in generic script');
          setupFormForDirectDetection(form, index);
          return;
        }
      } catch (e) {
        // Continue searching
      }
    }
  }
}

// Set up form interception for personalized variants
function setupFormInterception(form, variants) {
  // Prevent duplicate setup
  if (form.dataset.cardPersonalisationSetup === 'true') {
    console.log('Card Personalisation: Form already has personalisation setup, skipping:', form.id);
    return;
  }
  
  console.log('Card Personalisation: Setting up form interception for:', form.id);
  form.dataset.cardPersonalisationSetup = 'true';
  
  // Check initial state
  checkFormPersonalizationState(form, variants);
  
  // Listen for variant changes
  form.addEventListener('change', (event) => {
    if (event.target.matches('input[type="radio"], select')) {
      console.log('Card Personalisation: 🔄 Variant selection changed via:', event.target.type, event.target.name, 'value:', event.target.value);
      setTimeout(() => checkFormPersonalizationState(form, variants), 50);
    }
  });
  
  // Also listen for click events on radio buttons (more reliable)
  form.addEventListener('click', (event) => {
    if (event.target.matches('input[type="radio"]')) {
      console.log('Card Personalisation: 🖱️ Radio button clicked:', event.target.name, 'value:', event.target.value);
      setTimeout(() => checkFormPersonalizationState(form, variants), 100);
    }
  });
  
  // Listen for variant-selects custom events
  form.addEventListener('variant:changed', (event) => {
    console.log('Card Personalisation: 🎯 Custom variant:changed event detected');
    setTimeout(() => checkFormPersonalizationState(form, variants), 50);
  });
  
  // Also add click listeners to all buttons in the form for debugging
  const buttons = form.querySelectorAll('button');
  buttons.forEach(button => {
    button.addEventListener('click', (event) => {
      console.log('Card Personalisation: 🖱️ Button clicked:', button.textContent.trim(), 'type:', button.type, 'name:', button.name);
      
      if (button.type === 'submit' || button.name === 'add') {
        console.log('Card Personalisation: 🚨 Submit button clicked - checking personalization requirement');
        setTimeout(() => {
          const variantId = getSelectedVariantId(form);
          const productTitle = getProductTitleFromPage();
          const requiresPersonalisation = variantId && requiresPersonalisationByVariantId(variantId, variants, productTitle);
          console.log('Card Personalisation: Button click check - variant:', variantId, 'requires:', requiresPersonalisation);
          
          if (requiresPersonalisation) {
            console.log('Card Personalisation: 🛑 SHOULD INTERCEPT - will prevent form submission');
          } else {
            console.log('Card Personalisation: ✅ Normal submission allowed');
          }
        }, 10);
      }
    }, true);
  });
  
  // Intercept form submission
  console.log('🔧 SETUP: Adding submit listener to form:', form.id, form.className);
  form.addEventListener('submit', (event) => {
    console.log('Card Personalisation: 🚨 Form submission intercepted for form:', form.id);
    console.log('🚨 Form submission event details:', event);
    
      const variantId = getSelectedVariantId(form);
      console.log('Card Personalisation: Current variant ID:', variantId);
      
      const productTitle = getProductTitleFromPage();
      const requiresPersonalisation = variantId && requiresPersonalisationByVariantId(variantId, variants, productTitle);
      console.log('Card Personalisation: Requires personalisation:', requiresPersonalisation);
    
    if (requiresPersonalisation) {
      console.log('Card Personalisation: 🔄 Redirecting to personalisation page for variant:', variantId);
      event.preventDefault();
      event.stopImmediatePropagation();
      redirectToPersonalisationPage(form, variantId);
      return false;
    } else {
      console.log('Card Personalisation: No personalisation required, checking for express shipping and extra donation...');
      
      // Check for extra donation first
      if (handleExtraDonation(event, form)) {
        console.log('✅ handleExtraDonation returned true - took over');
        return false; // Extra donation handler took over
      }
      
      console.log('🔍 About to check handleExpressShipping');
      
      // Check if this is a personalized card form with express shipping selected
      if (handleExpressShipping(event, form)) {
        console.log('✅ handleExpressShipping returned true - took over');
        return false; // Express shipping handler took over
      }
      
      console.log('Card Personalisation: Allowing normal submission');
      console.log('✅ Allowing normal submission');
    }
  }, true); // Use capture phase to catch early
}

// Check if current form state requires personalization
function checkFormPersonalizationState(form, variants) {
  console.log('Card Personalisation: 🔍 Checking form personalization state...');
  
  const variantId = getSelectedVariantId(form);
  console.log('Card Personalisation: Current variant ID:', variantId);
  
  if (variantId) {
    const productTitle = getProductTitleFromPage();
    const requiresPersonalisation = requiresPersonalisationByVariantId(variantId, variants, productTitle);
    console.log('Card Personalisation: Variant', variantId, 'requires personalisation:', requiresPersonalisation);
    
    if (requiresPersonalisation) {
      console.log('Card Personalisation: ✅ Setting form to require personalisation');
      form.dataset.requiresPersonalisation = 'true';
      updateSubmitButtonText(form, 'Personalise Card');
      // Hide quantity selector for ecard/personalised variants
      hideQuantitySelector(form);
    } else {
      console.log('Card Personalisation: ✅ Setting form to NOT require personalisation');
      form.dataset.requiresPersonalisation = 'false';
      updateSubmitButtonText(form, null); // Reset to original
      // Show quantity selector for other variants (like blank card)
      showQuantitySelector(form);
    }
  } else {
    console.log('Card Personalisation: ⚠️ No variant ID found, assuming no personalisation required');
    form.dataset.requiresPersonalisation = 'false';
    updateSubmitButtonText(form, null); // Reset to original
  }
}

// Get currently selected variant ID from form
function getSelectedVariantId(form) {
  console.log('Card Personalisation: 🔍 Getting selected variant ID...');
  
  // Try variant ID input first
  const variantIdInput = form.querySelector('input[name="id"]');
  if (variantIdInput && variantIdInput.value) {
    console.log('Card Personalisation: ✅ Found variant ID in input[name="id"]:', variantIdInput.value);
    return variantIdInput.value;
  }
  
  // Try to get from variant selects
  const variantSelects = form.querySelector('variant-selects');
  if (variantSelects) {
    const selectedVariantScript = variantSelects.querySelector('script[data-selected-variant]');
    if (selectedVariantScript) {
      try {
        const variant = JSON.parse(selectedVariantScript.textContent);
        if (variant && variant.id) {
          console.log('Card Personalisation: ✅ Found variant ID in variant-selects:', variant.id);
          return variant.id;
        }
      } catch (error) {
        console.error('Card Personalisation: Error parsing selected variant:', error);
      }
    }
  }
  
  // Try to get from selected radio buttons (for card variant pickers)
  const selectedRadio = form.querySelector('input[type="radio"]:checked');
  if (selectedRadio && selectedRadio.value) {
    console.log('Card Personalisation: ✅ Found variant ID in checked radio:', selectedRadio.value);
    return selectedRadio.value;
  }
  
  // Try to get from select elements
  const selectElement = form.querySelector('select[name*="variant"], select[name="id"]');
  if (selectElement && selectElement.value) {
    console.log('Card Personalisation: ✅ Found variant ID in select:', selectElement.value);
    return selectElement.value;
  }
  
  console.log('Card Personalisation: ❌ No variant ID found');
  return null;
}

// Check if variant ID requires personalization
function requiresPersonalisationByVariantId(variantId, variants, productTitle = null) {
  const variant = variants.find(v => v.id == variantId);
  if (!variant) return false;
  
  const title = variant.title ? variant.title.toLowerCase() : '';
  const option1 = variant.option1 ? variant.option1.toLowerCase() : '';
  const option2 = variant.option2 ? variant.option2.toLowerCase() : '';
  const option3 = variant.option3 ? variant.option3.toLowerCase() : '';
  
  // Check if this is a BUNDLE product - bundles with print-at-home should NOT require personalisation
  const isBundleProduct = checkIfBundleProduct(productTitle);
  const isPrintAtHome = title.includes('print-at-home') || title.includes('print at home') ||
                        option1.includes('print-at-home') || option1.includes('print at home') ||
                        option2.includes('print-at-home') || option2.includes('print at home') ||
                        option3.includes('print-at-home') || option3.includes('print at home');
  
  // If it's a bundle product with print-at-home variant, skip personalisation
  if (isBundleProduct && isPrintAtHome) {
    console.log('Card Personalisation: BUNDLE product with Print at Home variant - skipping personalisation');
    return false;
  }
  
  const requiresPersonalisation = title.includes('personalised') || title.includes('e-card') || title.includes('ecard') || isPrintAtHome ||
                                  option1.includes('personalised') || option1.includes('e-card') || option1.includes('ecard') ||
                                  option2.includes('personalised') || option2.includes('e-card') || option2.includes('ecard') ||
                                  option3.includes('personalised') || option3.includes('e-card') || option3.includes('ecard');
  
  console.log('Card Personalisation: Variant check:', {
    id: variantId,
    title,
    option1,
    option2,
    option3,
    isBundleProduct,
    isPrintAtHome,
    requiresPersonalisation
  });
  
  return requiresPersonalisation;
}

// Get product title from page
function getProductTitleFromPage() {
  try {
    // Method 1: Check product title from page elements
    const titleSelectors = [
      '.product__title',
      '.product-single__title', 
      'h1.title',
      '.card-personalisation-product__info h3',
      'h1',
      '[data-product-title]'
    ];
    
    for (const selector of titleSelectors) {
      const titleElement = document.querySelector(selector);
      if (titleElement) {
        const title = titleElement.textContent.trim();
        if (title) {
          return title;
        }
      }
    }
    
    // Method 2: Check window.product if available
    if (window.product && window.product.title) {
      return window.product.title;
    }
    
    // Method 3: Check ShopifyAnalytics meta
    if (window.ShopifyAnalytics && window.ShopifyAnalytics.meta && window.ShopifyAnalytics.meta.product) {
      return window.ShopifyAnalytics.meta.product.name || window.ShopifyAnalytics.meta.product.title;
    }
    
    return null;
  } catch (error) {
    console.error('Card Personalisation: Error getting product title:', error);
    return null;
  }
}

// Check if current product is a BUNDLE product
function checkIfBundleProduct(productTitle = null) {
  try {
    // Method 1: Check provided product title
    if (productTitle && productTitle.toLowerCase().includes('bundle')) {
      console.log('Card Personalisation: Bundle detected via provided product title:', productTitle);
      return true;
    }
    
    // Method 2: Check product title from page elements
    const titleSelectors = [
      '.product__title',
      '.product-single__title', 
      'h1.title',
      '.card-personalisation-product__info h3',
      'h1',
      '[data-product-title]'
    ];
    
    for (const selector of titleSelectors) {
      const titleElement = document.querySelector(selector);
      if (titleElement) {
        const title = titleElement.textContent.trim().toLowerCase();
        if (title.includes('bundle')) {
          console.log('Card Personalisation: Bundle detected via page title element:', title);
          return true;
        }
      }
    }
    
    // Method 3: Check window.product if available
    if (window.product && window.product.title) {
      const title = window.product.title.toLowerCase();
      if (title.includes('bundle')) {
        console.log('Card Personalisation: Bundle detected via window.product.title:', title);
        return true;
      }
    }
    
    // Method 4: Check ShopifyAnalytics meta
    if (window.ShopifyAnalytics && window.ShopifyAnalytics.meta && window.ShopifyAnalytics.meta.product) {
      const productName = window.ShopifyAnalytics.meta.product.name || window.ShopifyAnalytics.meta.product.title;
      if (productName && productName.toLowerCase().includes('bundle')) {
        console.log('Card Personalisation: Bundle detected via ShopifyAnalytics:', productName);
        return true;
      }
    }
    
    return false;
  } catch (error) {
    console.error('Card Personalisation: Error checking bundle product:', error);
    return false;
  }
}

// Hide quantity selector for ecard/personalised variants
function hideQuantitySelector(form) {
  console.log('Card Personalisation: 🔍 Hiding quantity selector for form:', form.id);
  
  // Check if this specific form is for a Custom Price product - if so, don't hide quantity selector
  const isCustomPrice = checkIfCustomPriceProduct(form);
  if (isCustomPrice) {
    console.log('Card Personalisation: ⚠️ Custom Price product detected for this form - NOT hiding quantity selector');
    return;
  }
  
  // Reset quantity input to 1 before hiding
  const quantityInputs = [
    form.querySelector('input[name="quantity"]'),
    form.querySelector('.quantity__input'),
    form.querySelector('input[type="number"]')
  ];
  
  quantityInputs.forEach(input => {
    if (input && input.name === 'quantity') {
      input.value = '1';
      console.log('Card Personalisation: ✅ Reset quantity input to 1');
    }
  });
  
  // Only hide the specific quantity input elements, not the entire form sections
  const quantityElements = [
    form.querySelector('.product-form__quantity'),
    form.querySelector('div[id^="quantity-selector-"]'),
    form.querySelector('.quantity'),
    form.querySelector('quantity-input')
  ];
  
  quantityElements.forEach(element => {
    if (element && element.style.display !== 'none') {
      element.style.display = 'none';
      console.log('Card Personalisation: ✅ Hidden quantity element:', element.className);
    }
  });
  
  // Don't hide the entire QuantityForm div as it contains the Add to Cart button
  // Instead, just hide quantity-specific elements within it
  const quantityForm = form.querySelector('div[id^="QuantityForm-"]');
  if (quantityForm) {
    const quantityOnlyElements = quantityForm.querySelectorAll('.product-form__quantity, .quantity, quantity-input');
    quantityOnlyElements.forEach(element => {
      if (element.style.display !== 'none') {
        element.style.display = 'none';
        console.log('Card Personalisation: ✅ Hidden quantity-only element:', element.className);
      }
    });
  }
}

// Show quantity selector for other variants
function showQuantitySelector(form) {
  console.log('Card Personalisation: 🔍 Showing quantity selector for form:', form.id);
  
  // Check if this is a Custom Price product - if so, quantity should already be visible
  const isCustomPrice = checkIfCustomPriceProduct(form);
  if (isCustomPrice) {
    console.log('Card Personalisation: ⚠️ Custom Price product detected - quantity should already be visible');
    return;
  }
  
  // Show the specific quantity input elements
  const quantityElements = [
    form.querySelector('.product-form__quantity'),
    form.querySelector('div[id^="quantity-selector-"]'),
    form.querySelector('.quantity'),
    form.querySelector('quantity-input')
  ];
  
  quantityElements.forEach(element => {
    if (element) {
      // Force show even if not currently hidden - override any CSS
      element.style.display = '';
      element.style.visibility = 'visible';
      console.log('Card Personalisation: ✅ Shown quantity element:', element.className || element.id);
    }
  });
  
  // Show quantity-specific elements within the form
  const quantityForm = form.querySelector('div[id^="QuantityForm-"]');
  if (quantityForm) {
    const quantityOnlyElements = quantityForm.querySelectorAll('.product-form__quantity, .quantity, quantity-input');
    quantityOnlyElements.forEach(element => {
      // Force show even if not currently hidden - override any CSS
      element.style.display = '';
      element.style.visibility = 'visible';
      console.log('Card Personalisation: ✅ Shown quantity-only element:', element.className || element.id);
    });
  }
}

// Update submit button text
function updateSubmitButtonText(form, newText) {
  const submitButton = form.querySelector('button[name="add"]') || 
                      form.querySelector('button[type="submit"]') ||
                      form.querySelector('.product-form__submit');
  
  if (submitButton) {
    console.log('Card Personalisation: 🔍 Found submit button:', submitButton.className);
    
    const buttonTextElement = submitButton.querySelector('span') || submitButton;
    console.log('Card Personalisation: 🔍 Button text element:', buttonTextElement.tagName, buttonTextElement.className);
    
    console.log('Card Personalisation: 🔄 Button text update - Current:', buttonTextElement.textContent.trim(), 'New:', newText, 'Original stored:', buttonTextElement.dataset.originalText);
    
    if (newText) {
      // Store original text only once, when first changing from default
      if (!buttonTextElement.dataset.originalText) {
        const currentText = buttonTextElement.textContent.trim();
        // Only store if it's not already a personalisation text
        if (!currentText.includes('Personalise')) {
          buttonTextElement.dataset.originalText = currentText;
          console.log('Card Personalisation: 📝 Stored original button text:', currentText);
        }
      }
      buttonTextElement.textContent = newText;
      console.log('Card Personalisation: ✅ Updated button text to:', newText, '- Actual content now:', buttonTextElement.textContent.trim());
    } else {
      // Restore to original text or default
      const originalText = buttonTextElement.dataset.originalText || 'Add to cart';
      buttonTextElement.textContent = originalText;
      console.log('Card Personalisation: ↩️ Restored button text to:', originalText, '- Actual content now:', buttonTextElement.textContent.trim());
    }
  } else {
    console.log('Card Personalisation: ❌ No submit button found in form - tried selectors:', 
      'button[name="add"]', 'button[type="submit"]', '.product-form__submit');
  }
}

// Redirect to personalization page
function redirectToPersonalisationPage(form, variantId) {
  console.log('Card Personalisation: 🔄 Starting redirect process...');
  
  const productId = getProductIdFromForm(form);
  const quantity = form.querySelector('input[name="quantity"]')?.value || '1';
  
  console.log('Card Personalisation: Debug redirect data:', {
    form: form.id || '[no id]',
    productId,
    variantId,
    quantity,
    formAction: form.action,
    currentURL: window.location.href
  });
  
  if (!productId) {
    console.error('Card Personalisation: ❌ No product ID found, cannot redirect');
    console.error('Card Personalisation: ❌ Tried form action, input[name="product-id"], URL, and data attributes');
    return;
  }
  
  if (!variantId) {
    console.error('Card Personalisation: ❌ No variant ID provided, cannot redirect');
    return;
  }
  
  // Ensure IDs are valid
  const cleanProductId = String(productId).trim();
  const cleanVariantId = String(variantId).trim();
  
  if (!cleanProductId || cleanProductId === 'null' || cleanProductId === 'undefined') {
    console.error('Card Personalisation: ❌ Invalid product ID:', productId);
    return;
  }
  
  if (!cleanVariantId || cleanVariantId === 'null' || cleanVariantId === 'undefined') {
    console.error('Card Personalisation: ❌ Invalid variant ID:', variantId);
    return;
  }
  
  // Put variant_id last to test if only last parameter is read
  const url = `/pages/card-personalisation?quantity=${quantity}&product_id=${cleanProductId}&variant_id=${cleanVariantId}`;
  console.log('Card Personalisation: ✅ Redirecting to (variant_id last):', url);
  console.log('Card Personalisation: Full redirect URL:', window.location.origin + url);
  
  // Add a small delay to ensure console logs are visible
  setTimeout(() => {
    window.location.href = url;
  }, 100);
}

// Get product ID from form
function getProductIdFromForm(form) {
  console.log('Card Personalisation: Getting product ID from form:', form.id);
  console.log('Card Personalisation: Form action:', form.action);
  console.log('Card Personalisation: Current URL:', window.location.href);
  
  // Try to get from form action
  const actionMatch = form.action.match(/\/products\/([^\/]+)/);
  if (actionMatch) {
    console.log('Card Personalisation: Product ID from form action:', actionMatch[1]);
    return actionMatch[1];
  }
  
  // Try to get from product data
  const productInput = form.querySelector('input[name="product-id"]');
  if (productInput) {
    console.log('Card Personalisation: Product ID from input:', productInput.value);
    return productInput.value;
  }
  
  // Try to get from URL
  const urlMatch = window.location.pathname.match(/\/products\/([^\/]+)/);
  if (urlMatch) {
    console.log('Card Personalisation: Product ID from URL:', urlMatch[1]);
    return urlMatch[1];
  }
  
  // Try to get from data attribute
  const productIdFromData = form.dataset.productId || form.getAttribute('data-product-id');
  if (productIdFromData) {
    console.log('Card Personalisation: Product ID from data attribute:', productIdFromData);
    return productIdFromData;
  }
  
  console.log('Card Personalisation: ❌ No product ID found');
  return null;
}

// Export for external use
window.CardPersonalisationHandler = CardPersonalisationHandler;

// Manual trigger function for testing
window.forceCardPersonalisationSetup = function() {
  console.log('🔧 Manually triggering card personalisation setup...');
  
  // Find variant-selects and add class if missing
  const variantSelects = document.querySelectorAll('variant-selects');
  console.log('Found', variantSelects.length, 'variant-selects elements');
  
  variantSelects.forEach((el, i) => {
    console.log(`Element ${i}:`, el.className);
    if (!el.classList.contains('card-variant-picker')) {
      el.classList.add('card-variant-picker');
      console.log('✅ Added card-variant-picker class');
    }
  });
  
  // Trigger setup
  setTimeout(() => {
    setupCardVariantPickerIntegration();
    
    // Also trigger direct variant detection
    console.log('Manual trigger: Also starting direct variant detection...');
    setupDirectVariantDetection();
  }, 100);
};

// Test form submission interception
window.testFormInterception = function() {
  console.log('🧪 Testing form submission interception...');
  
  const forms = document.querySelectorAll('form');
  console.log('Found', forms.length, 'forms');
  
  forms.forEach((form, i) => {
    console.log(`Form ${i}:`, {
      id: form.id,
      action: form.action,
      hasSubmitButton: !!form.querySelector('button[type="submit"]'),
      hasAddButton: !!form.querySelector('button[name="add"]'),
      hasVariantId: !!form.querySelector('input[name="id"]'),
      interceptorAttached: form.dataset.cardPersonalisationSetup === 'true'
    });
  });
  
  // Force setup direct detection
  setupDirectVariantDetection();
};

// Test current personalization state
window.testPersonalizationState = function() {
  console.log('🧪 Testing current personalization state...');
  
  const mainForm = document.querySelector('form[action*="/cart/add"]');
  if (!mainForm) {
    console.log('❌ No cart/add form found');
    return;
  }
  
  console.log('📋 Main form:', mainForm.id);
  
  const variantId = getSelectedVariantId(mainForm);
  console.log('🔢 Current variant ID:', variantId);
  
  // Get variants data
  const productVariantsScript = document.querySelector('script[data-product-variants]');
  if (productVariantsScript) {
    try {
      const variants = JSON.parse(productVariantsScript.textContent);
      console.log('📊 Total variants:', variants.length);
      
      const currentVariant = variants.find(v => v.id == variantId);
      if (currentVariant) {
        console.log('🎯 Current variant:', {
          id: currentVariant.id,
          title: currentVariant.title,
          option1: currentVariant.option1,
          option2: currentVariant.option2,
          option3: currentVariant.option3
        });
        
        const productTitle = getProductTitleFromPage();
        const requiresPersonalisation = requiresPersonalisationByVariantId(variantId, variants, productTitle);
        console.log('❓ Requires personalization:', requiresPersonalisation);
        
        if (requiresPersonalisation) {
          console.log('✅ This variant SHOULD redirect to personalization page');
        } else {
          console.log('ℹ️ This variant does NOT need personalization');
        }
      } else {
        console.log('❌ Current variant not found in variants list');
      }
    } catch (e) {
      console.error('❌ Error parsing variants:', e);
    }
  } else {
    console.log('❌ No product variants script found');
  }
};

// Debug helper for testing
window.debugCardPersonalisation = function() {
  console.log('=== Card Personalisation Debug ===');
  
  // Try all the same selectors as the main function
  const selectors = [
    '.card-variant-picker',
    'variant-selects.card-variant-picker',
    '[class*="card-variant-picker"]'
  ];
  
  let cardVariantPickers = [];
  for (const selector of selectors) {
    const found = document.querySelectorAll(selector);
    console.log(`Debug: Selector "${selector}" found ${found.length} elements`);
    if (found.length > 0) {
      cardVariantPickers = found;
      break;
    }
  }
  
  console.log('Found', cardVariantPickers.length, 'card variant pickers');
  
  // Debug ALL variant-selects elements
  const allVariantSelects = document.querySelectorAll('variant-selects');
  console.log('Debug: ALL variant-selects elements:', allVariantSelects.length);
  allVariantSelects.forEach((el, i) => {
    console.log(`Debug: variant-selects[${i}]:`, {
      tagName: el.tagName,
      className: el.className,
      id: el.id,
      classList: Array.from(el.classList),
      hasCardVariantPickerClass: el.classList.contains('card-variant-picker')
    });
  });
  
  Array.from(cardVariantPickers).forEach((picker, index) => {
    console.log(`Card variant picker ${index}:`, picker);
    
    const selectedInputs = picker.querySelectorAll('input[type="radio"]:checked');
    console.log('Selected inputs:', Array.from(selectedInputs).map(i => i.value));
    
    const variantsScript = picker.querySelector('script[data-product-variants]');
    if (variantsScript) {
      const variants = JSON.parse(variantsScript.textContent);
      console.log('Available variants:', variants.length);
      variants.forEach(v => {
        console.log(`  - ${v.id}: ${v.title} (${v.options || [v.option1, v.option2, v.option3].filter(Boolean).join(', ')})`);
      });
    } else {
      console.log('No variants data found');
    }
  });
  
  const productForms = document.querySelectorAll('form');
  console.log('Found', productForms.length, 'total forms');
  
  productForms.forEach((form, index) => {
    console.log(`Debug Form ${index}:`, {
      element: form,
      tagName: form.tagName,
      id: form.id,
      className: form.className,
      hasVariantInput: !!form.querySelector('[name="id"]'),
      hasAddButton: !!form.querySelector('[name="add"]')
    });
    
    if (form.querySelector('[name="id"]') || form.querySelector('[name="add"]') || form.id.includes('product')) {
      const variantIdInput = form.querySelector('input[name="id"]');
      console.log(`  Variant ID = ${variantIdInput ? variantIdInput.value : 'not found'}`);
      console.log(`  Requires personalisation: ${form.dataset.requiresPersonalisation}`);
    }
  });
  
  console.log('=== End Debug ===');
};

// Manual test function
window.testCardPersonalisation = function() {
  console.log('=== Testing Card Personalisation ===');
  
  const forms = document.querySelectorAll('form');
  let productForm = null;
  
  for (const form of forms) {
    if (form.querySelector('[name="id"]') && form.querySelector('[name="add"]')) {
      productForm = form;
      break;
    }
  }
  
  if (!productForm) {
    console.log('No product form found!');
    return;
  }
  
  console.log('Testing with form:', productForm.id);
  
  const handler = window.cardPersonalisationHandler || new CardPersonalisationHandler();
  const requiresPersonalisation = handler.requiresPersonalisation(productForm);
  
  console.log('Requires personalisation:', requiresPersonalisation);
  
  if (requiresPersonalisation) {
    console.log('Triggering personalisation redirect...');
    handler.redirectToPersonalisation(productForm);
  } else {
    console.log('Form does not require personalisation');
  }
};

// Force personalisation test - bypasses all checks
window.forcePersonalisation = function() {
  console.log('🚨 FORCING PERSONALISATION REDIRECT 🚨');
  
  const forms = document.querySelectorAll('form');
  let productForm = null;
  
  for (const form of forms) {
    if (form.querySelector('[name="id"]')) {
      productForm = form;
      break;
    }
  }
  
  if (!productForm) {
    console.log('❌ No product form found!');
    return;
  }
  
  console.log('✅ Found form:', productForm.id);
  
  // Force mark as requiring personalisation
  productForm.dataset.requiresPersonalisation = 'true';
  
  const handler = window.cardPersonalisationHandler || new CardPersonalisationHandler();
  
  console.log('🎯 Triggering redirect...');
  handler.redirectToPersonalisation(productForm);
};

// Test variant detection 
window.testVariantDetection = function() {
  console.log('=== Testing Variant Detection ===');
  
  const forms = document.querySelectorAll('form');
  let productForm = null;
  
  for (const form of forms) {
    if (form.querySelector('[name="id"]') && form.querySelector('[name="add"]')) {
      productForm = form;
      break;
    }
  }
  
  if (!productForm) {
    console.log('❌ No product form found!');
    return;
  }
  
  console.log('✅ Found product form:', productForm.id);
  
  // Check variant ID in form
  const variantIdInput = productForm.querySelector('input[name="id"]');
  console.log('Variant ID input:', variantIdInput ? variantIdInput.value : 'NOT FOUND');
  
  // Check card picker state
  const cardPicker = document.querySelector('.card-variant-picker');
  if (cardPicker) {
    const selectedInputs = cardPicker.querySelectorAll('input:checked');
    console.log('Card picker selections:', Array.from(selectedInputs).map(i => i.value));
    
    if (selectedInputs.length > 0) {
      const selectedValues = Array.from(selectedInputs).map(input => input.value);
      const matchingVariant = findMatchingVariant(selectedValues);
      console.log('Matching variant:', matchingVariant);
    }
  } else {
    console.log('❌ No card variant picker found');
  }
};

// Check current form state
window.checkFormState = function() {
  console.log('=== Current Form State ===');
  
  const forms = document.querySelectorAll('form');
  forms.forEach((form, index) => {
    if (form.querySelector('[name="id"]') || form.id.includes('product')) {
      console.log(`Form ${index}: ${form.id}`);
      console.log(`  Requires personalisation: ${form.dataset.requiresPersonalisation}`);
      
      const variantIdInput = form.querySelector('input[name="id"]');
      console.log(`  Variant ID: ${variantIdInput ? variantIdInput.value : 'not found'}`);
      
      const submitButton = form.querySelector('button[type="submit"]');
      console.log(`  Submit button text: ${submitButton ? submitButton.textContent.trim() : 'not found'}`);
    }
  });
  
  // Check card variant picker state
  const cardPicker = document.querySelector('.card-variant-picker');
  if (cardPicker) {
    const selectedInputs = cardPicker.querySelectorAll('input:checked');
    console.log('Card picker selections:', Array.from(selectedInputs).map(i => i.value));
  } else {
    console.log('No card variant picker found');
  }
};

// ============ Express Shipping & Extra Donation Handling ============
console.log('🧪 EXPRESS SHIPPING & DONATION CODE LOADED IN ASSET FILE - ' + new Date().toISOString());

function handleExpressShipping(event, form) {
  console.log('🚚 Checking for express shipping...');
  
  // Check if we're on the card personalization page
  const isPersonalizationPage = window.location.pathname.includes('card-personalisation') || 
                                window.location.pathname.includes('card-personalization');
  console.log('🔍 Is personalization page:', isPersonalizationPage);
  
  // SKIP asset file handling for personalization page - let template handle it
  if (isPersonalizationPage) {
    console.log('🚚 Personalization page detected - deferring to template logic');
    return false; // Let template file handle express shipping
  }
  
  // Also check if this looks like a personalized card form (has personalization fields)
  const hasPersonalizationFields = form.querySelector('textarea[name*="Personalised Message"]') ||
                                   form.querySelector('input[name*="Recipient Name"]') ||
                                   form.querySelector('input[name*="Sender Name"]');
  console.log('🔍 Has personalization fields:', !!hasPersonalizationFields);
  
  if (!hasPersonalizationFields) {
    console.log('🚚 Not a personalization form, skipping express shipping check');
    return false;
  }
  
  // Check if this is an ecard (shipping sections are hidden)
  const shippingSection = document.getElementById('shipping-method-section');
  if (shippingSection && shippingSection.style.display === 'none') {
    console.log('🔍 This is an ecard - no shipping options available');
    return false; // No express shipping for ecards
  }
  
  // Check if express shipping is selected
  let expressShipping = document.getElementById('express-post');
  
  // Fallback: try to find by name attribute if ID doesn't work
  if (!expressShipping) {
    console.log('⚠️ Express shipping element not found by ID, trying by name...');
    expressShipping = form.querySelector('input[name="properties[Shipping Method]"][value*="Express"]');
  }
  
  console.log('🔍 Express shipping element:', expressShipping);
  console.log('🔍 Express shipping checked:', expressShipping ? expressShipping.checked : 'element not found');
  
  if (expressShipping && expressShipping.checked) {
    console.log('✅ Express shipping is selected! Handling custom submission...');
    
    // Prevent default form submission
    event.preventDefault();
    event.stopImmediatePropagation();
    
    console.log('🚚 Adding personalized card with express shipping...');
    // Handle the submission with express shipping
    handlePersonalizedCardWithExpressShipping(form);
    return true; // We handled it
  } else {
    console.log('🚚 Express shipping not selected, allowing normal submission');
    return false; // Let normal submission continue
  }
}

function handlePersonalizedCardWithExpressShipping(form) {
  console.log('🚚 Adding personalized card with express shipping...');
  
  // Create FormData from the form
  const formData = new FormData(form);
  
  // Note: Express shipping is added as a separate line item
  
  // Debug: Log all form data
  console.log('🔍 Form data:');
  for (let [key, value] of formData.entries()) {
    console.log(`  ${key}: ${value}`);
  }
  
  // First, add the main product to cart
  fetch('/cart/add.js', {
    method: 'POST',
    body: formData
  })
  .then(response => response.json())
  .then(data => {
    if (data.status) {
      throw new Error(data.description || 'Failed to add to cart');
    }
    
    console.log('✅ Personalized card added to cart successfully');
    
    // Now add express shipping as separate product
    return addExpressShippingToCart();
  })
  .then(() => {
    console.log('🛒 Redirecting to cart with both items...');
    window.location.href = '/cart';
  })
  .catch(error => {
    console.error('❌ Error:', error);
    console.error('❌ Error adding to cart:', error.message);
  });
}

// ============ Extra Donation Handling ============
function handleExtraDonation(event, form) {
  console.log('💝 Checking for extra donation...');
  
  const donationSection = document.getElementById('extra-donation-section');
  if (!donationSection) {
    console.log('💝 No donation section found');
    return false;
  }
  
  const selectedDonation = donationSection.querySelector('input[name="extra_donation"]:checked');
  if (!selectedDonation) {
    console.log('💝 No donation selected');
    return false;
  }
  
  let variantId, amount;
  
  if (selectedDonation.value === 'custom') {
    const customInput = document.getElementById('custom-donation-amount');
    const customValue = customInput ? parseFloat(customInput.value) : 0;
    
    if (customValue <= 0) {
      console.log('💝 Invalid custom donation amount');
      return false;
    }
    
    // Use first available variant for custom amounts
    variantId = donationSection.querySelector('input[name="extra_donation"]:not([value="custom"])')?.value;
    if (!variantId) {
      console.log('💝 No donation variant available for custom amount');
      return false;
    }
    amount = customValue;
  } else {
    variantId = selectedDonation.value;
    amount = parseFloat(selectedDonation.dataset.price) / 100;
  }
  
  console.log('💝 Extra donation selected:', { variantId, amount });
  
  // Prevent default form submission
  event.preventDefault();
  event.stopImmediatePropagation();
  
  // Handle the main product + donation submission
  handleProductWithExtraDonation(form, variantId, amount);
  return true;
}

function handleProductWithExtraDonation(form, donationVariantId, donationAmount) {
  console.log('💝 Adding product with extra donation...');
  
  // Get main product form data
  const mainFormData = new FormData(form);
  
  // Add donation properties to main product
  mainFormData.append('properties[_has_extra_donation]', 'true');
  mainFormData.append('properties[_donation_amount]', donationAmount.toString());
  
  // Submit main product first
  fetch('/cart/add.js', {
    method: 'POST',
    body: mainFormData
  })
  .then(response => response.json())
  .then(data => {
    if (data.status) {
      throw new Error(data.description || 'Failed to add main product to cart');
    }
    
    console.log('✅ Main product added to cart:', data);
    
    // Now add the donation as separate product
    return addExtraDonationToCart(donationVariantId, donationAmount);
  })
  .then(() => {
    console.log('🛒 Redirecting to cart with both items...');
    window.location.href = '/cart';
  })
  .catch(error => {
    console.error('❌ Error:', error);
    console.error('❌ Error adding product with donation:', error.message);
  });
}

function addExtraDonationToCart(variantId, amount) {
  console.log('💝 Adding extra donation to cart...', { variantId, amount });
  
  const DONATION_PRODUCT_ID = '9621667905893';
  
  const donationFormData = new FormData();
  donationFormData.append('id', variantId);
  donationFormData.append('quantity', '1');
  donationFormData.append('properties[_extra_donation]', 'true');
  donationFormData.append('properties[_donation_amount]', amount.toString());
  donationFormData.append('properties[Donation Amount]', '$' + amount.toFixed(2));
  donationFormData.append('properties[_linked_to_main_product]', 'true');
  
  return fetch('/cart/add.js', {
    method: 'POST',
    body: donationFormData
  })
  .then(response => response.json())
  .then(data => {
    if (data.status) {
      throw new Error(data.description || 'Failed to add extra donation to cart');
    }
    
    console.log('✅ Extra donation added to cart:', data);
    return data;
  });
}

function addExpressShippingToCart() {
  console.log('🚚 Adding express shipping product to cart...');
  
  const EXPRESS_SHIPPING_PRODUCT_ID = '9621634220389';
  const EXPRESS_SHIPPING_VARIANT_ID = '49912445501797';
  
  // Create form data for express shipping product
  const expressShippingFormData = new FormData();
  expressShippingFormData.append('id', EXPRESS_SHIPPING_VARIANT_ID);
  expressShippingFormData.append('quantity', '1');
  expressShippingFormData.append('properties[_express_shipping]', 'true');
  expressShippingFormData.append('properties[_linked_to_personalized_card]', 'true');
  
  console.log('🔍 Adding express shipping variant ID:', EXPRESS_SHIPPING_VARIANT_ID);
  
  return fetch('/cart/add.js', {
    method: 'POST',
    body: expressShippingFormData
  })
  .then(response => {
    console.log('🔍 Express shipping add response status:', response.status);
    return response.json();
  })
  .then(data => {
    if (data.status) {
      throw new Error(data.description || 'Failed to add express shipping to cart');
    }
    console.log('✅ Express shipping product added to cart:', data);
    return data;
  });
}

// Check if current product is a Custom Price product
function checkIfCustomPriceProduct(form = null) {
  try {
    // If a specific form is provided, check within that form's context
    if (form) {
      // Method 1: Check if there's a product-form__quantity--custom-price class within this form
      const customPriceElement = form.querySelector('.product-form__quantity--custom-price');
      if (customPriceElement) {
        console.log('Card Personalisation: Custom Price detected via CSS class in form:', form.id);
        return true;
      }
      
      // Method 2: Check if there's a "Choose your Impact" label within this form
      const impactLabels = form.querySelectorAll('label .font-body-bold');
      for (const label of impactLabels) {
        if (label.textContent.trim() === 'Choose your Impact') {
          console.log('Card Personalisation: Custom Price detected via "Choose your Impact" label in form:', form.id);
          return true;
        }
      }
      
      console.log('Card Personalisation: Not a Custom Price product for form:', form.id);
      return false;
    }
    
    // Fallback: Global check if no form provided
    // Method 1: Check if there's a product-form__quantity--custom-price class
    const customPriceElement = document.querySelector('.product-form__quantity--custom-price');
    if (customPriceElement) {
      console.log('Card Personalisation: Custom Price detected via CSS class (global)');
      return true;
    }
    
    // Method 2: Check if there's a "Choose your Impact" label
    const impactLabels = document.querySelectorAll('label .font-body-bold');
    for (const label of impactLabels) {
      if (label.textContent.trim() === 'Choose your Impact') {
        console.log('Card Personalisation: Custom Price detected via "Choose your Impact" label (global)');
        return true;
      }
    }
    
    // Method 3: Check if product type is available in window.product or similar
    if (window.product && window.product.metafields && window.product.metafields.custom && window.product.metafields.custom.custom_price) {
      console.log('Card Personalisation: Custom Price detected via window.product.metafields.custom.custom_price (global)');
      return true;
    }
    
    console.log('Card Personalisation: Not a Custom Price product (global)');
    return false;
  } catch (error) {
    console.error('Card Personalisation: Error checking Custom Price product:', error);
    return false;
  }
} 